import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { formatMoney } from "../../lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Trash2 } from "lucide-react";

export function AdminGuestsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/admin/guests").then((r) => { setRows(r.data); setLoading(false); });
  }, []);

  const filtered = rows.filter((g) =>
    !filter ||
    (g.name || "").toLowerCase().includes(filter.toLowerCase()) ||
    (g.email || "").toLowerCase().includes(filter.toLowerCase()) ||
    (g.phone || "").toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div data-testid="admin-guests-page">
      <div className="mb-8">
        <span className="text-[11px] uppercase tracking-[0.28em] text-[#22302E]/60">CRM</span>
        <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Hóspedes</h1>
        <p className="text-sm text-[#22302E]/80 mt-1">Base agregada por e-mail, com histórico completo de reservas e observações administrativas.</p>
      </div>

      <div className="mb-4">
        <Input placeholder="Buscar por nome, e-mail ou telefone" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="guest-search" />
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4EF] border-b border-[#E4E4E7]">
            <tr className="text-left">
              <Th>Nome</Th><Th>E-mail</Th><Th>Telefone</Th><Th>Reservas</Th><Th>Confirmadas</Th><Th>LTV</Th><Th>Última reserva</Th><Th></Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F7F4EF]">
            {loading && <tr><td colSpan={8} className="text-center py-12 text-[#22302E]/60">Carregando...</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-[#22302E]/60">Nenhum hóspede encontrado.</td></tr>}
            {filtered.map((g) => (
              <tr key={g.email} className="hover:bg-[#F7F4EF]" data-testid={`guest-row-${g.email}`}>
                <Td className="font-medium text-[#22302E]">{g.name || "—"}</Td>
                <Td className="text-xs">{g.email}</Td>
                <Td className="text-xs">{g.phone || "—"}</Td>
                <Td>{g.reservations}</Td>
                <Td>{g.confirmed}</Td>
                <Td>{formatMoney(g.lifetime_value || 0)}</Td>
                <Td className="text-xs">{g.last_reservation?.substring(0, 10) || "—"}</Td>
                <Td className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => nav(`/admin/hospedes/${encodeURIComponent(g.email)}`)} data-testid={`view-guest-${g.email}`}>
                    Perfil
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminGuestProfile() {
  const { email } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  async function load() {
    const r = await api.get(`/admin/guests/${encodeURIComponent(email)}`);
    setData(r.data);
    setNotes(r.data.notes || "");
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [email]);

  async function saveNotes() {
    setSavingNotes(true);
    try {
      await api.patch(`/admin/guests/${encodeURIComponent(email)}`, { notes });
      toast.success("Observações salvas");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setSavingNotes(false); }
  }

  async function lgpdDelete() {
    try {
      await api.delete(`/admin/guests/${encodeURIComponent(email)}`);
      toast.success("Dados pessoais removidos (LGPD)");
      nav("/admin/hospedes");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  }

  if (!data) return <div className="text-[#22302E]/60">Carregando...</div>;

  return (
    <div data-testid="admin-guest-profile">
      <button onClick={() => nav("/admin/hospedes")} className="text-sm text-[#066B63] flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      <div className="mb-8">
        <span className="text-[11px] uppercase tracking-[0.28em] text-[#22302E]/60">Perfil do hóspede</span>
        <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">{data.name}</h1>
        <div className="text-sm text-[#22302E]/80 mt-2">
          {data.email} · {data.phone || "—"} · {data.reservations_count} reserva(s) · LTV {formatMoney(data.lifetime_value)}
        </div>
      </div>

      <section className="bg-white border border-[#E4E4E7] rounded-md p-5 mb-6">
        <div className="text-xs uppercase tracking-[0.24em] text-[#22302E]/60 mb-3">Observações administrativas</div>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Preferências, alergias, ocorrências, informações relevantes para próximas estadias." data-testid="guest-notes" />
        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-[#22302E]/60">
            {data.notes_updated_at && <>Última atualização em {data.notes_updated_at.substring(0, 10)} por {data.notes_updated_by}</>}
          </div>
          <Button onClick={saveNotes} disabled={savingNotes} className="bg-[#0AA89C] hover:bg-[#066B63] text-white" data-testid="save-notes">
            {savingNotes ? "Salvando..." : "Salvar observações"}
          </Button>
        </div>
      </section>

      <section className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-[#E4E4E7] text-xs uppercase tracking-[0.24em] text-[#22302E]/60">Histórico de reservas</div>
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4EF] border-b border-[#E4E4E7]">
            <tr className="text-left">
              <Th>Código</Th><Th>Casa</Th><Th>Check-in</Th><Th>Check-out</Th><Th>Canal</Th><Th>Total</Th><Th>Status</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F7F4EF]">
            {data.reservations.map((r) => (
              <tr key={r.id}>
                <Td className="font-mono text-xs">{r.code}</Td>
                <Td>{r.house_name}</Td>
                <Td>{r.check_in}</Td>
                <Td>{r.check_out}</Td>
                <Td className="text-xs">{r.channel}</Td>
                <Td>{formatMoney(r.total)}</Td>
                <Td className="text-xs">{r.status}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="bg-white border border-[#E4E4E7] rounded-md p-5 border-l-4 border-l-red-500">
        <div className="text-xs uppercase tracking-[0.24em] text-red-700 mb-2">Direito ao esquecimento (LGPD)</div>
        <p className="text-sm text-[#22302E]/80 mb-3">
          Ao solicitação do titular, todos os dados pessoais deste hóspede podem ser removidos. As reservas históricas
          são mantidas, com nome/e-mail/telefone anonimizados para preservar registros contábeis.
        </p>
        <Button variant="outline" onClick={() => setConfirmDel(true)} className="border-red-500 text-red-700 hover:bg-red-50" data-testid="lgpd-delete-btn">
          <Trash2 className="w-4 h-4 mr-2" /> Excluir dados pessoais
        </Button>
      </section>

      <Dialog open={confirmDel} onOpenChange={setConfirmDel}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Excluir dados pessoais</DialogTitle>
            <DialogDescription>Esta ação é irreversível. O histórico de reservas será preservado, mas nome, e-mail e telefone serão substituídos por um marcador de anonimização.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmDel(false)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={lgpdDelete} data-testid="confirm-lgpd-delete">Confirmar exclusão</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#22302E]/60 font-semibold">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 text-[#22302E] ${className}`}>{children}</td>; }
